import React, { useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Puzzles.module.css';
import globalStyles from '../../App.module.css';
import Board from '../Board';
import { GameContext } from '../Providers/GameContext';
import { useTheme } from '../Providers/ThemeContext';
import { useLanguage } from '../Providers/LanguageContext';
import { AuthContext } from '../Providers/AuthContext';
import GlobalHeader from '../GlobalHeader';
import AdSenseLayout from '../AdSense/AdSenseLayout';
import WispyStars from '../Tutorials/WispyStars';
import { Mode } from '../../services/zodiacEngine/types';
import ZodiacEngineService from '../ZodiacEngineService';
import { ZodiacFEN } from '../../services/zodiacEngine/Fen/ZodiacFEN';
import { SavedPuzzle, MoveRound, MoveStep, getRandomUnseenPuzzle } from '../../services/firebase/puzzleService';
import { UserDataService } from '../../services/firebase/userDataService';
import { playUISound, playVictorySound } from '../../utils/UISoundEffects';

type PuzzleTypeId = 'bestMove' | 'kingGuesser';

const PUZZLE_TYPES: { id: PuzzleTypeId; title: string; description: string }[] = [
  {
    id: 'bestMove',
    title: 'Find the Best Move',
    description: 'Study a position and find the strongest move.',
  },
  {
    id: 'kingGuesser',
    title: 'Guess the King',
    description: 'Watch the replay and identify the king.',
  },
];

/** Normalize legacy single-move puzzles into the MoveRound[] format */
function getNormalizedMoveSequence(puzzle: SavedPuzzle): MoveRound[] | null {
  if (puzzle.moveSequence && puzzle.moveSequence.length > 0) {
    return puzzle.moveSequence;
  }
  if (puzzle.moveFrom && puzzle.moveTo && puzzle.movePieceType !== undefined) {
    return [{
      playerMove: {
        pieceType: puzzle.movePieceType,
        pieceIsLight: puzzle.movePieceIsLight ?? true,
        from: puzzle.moveFrom,
        to: puzzle.moveTo,
      },
    }];
  }
  return null;
}

function Puzzles() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const { gameState, potentialMoves, potentialSideEffects, updateGameState } = useContext(GameContext);
  const { user, userProfile } = useContext(AuthContext);

  const [selectedType, setSelectedType] = useState<PuzzleTypeId | null>(null);
  const [currentPuzzle, setCurrentPuzzle] = useState<SavedPuzzle | null>(null);
  const [loading, setLoading] = useState(false);

  // Puzzle interaction state
  const [moveSequence, setMoveSequence] = useState<MoveRound[] | null>(null);
  const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
  const [wrongMovePos, setWrongMovePos] = useState<{ row: number; col: number } | null>(null);
  const [correctMovePos, setCorrectMovePos] = useState<{ row: number; col: number } | null>(null);
  const [puzzleStatus, setPuzzleStatus] = useState<'idle' | 'playing' | 'complete'>('idle');
  const [checkpointFen, setCheckpointFen] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [animatingOpponent, setAnimatingOpponent] = useState(false);

  // King Guesser state
  const [kingGuessPhase, setKingGuessPhase] = useState<'countdown' | 'replay' | 'guessing' | null>(null);
  const [countdownNumber, setCountdownNumber] = useState<number | null>(null);
  const [guessedPos, setGuessedPos] = useState<{ row: number; col: number } | null>(null);

  // Refs for move detection
  const lastProcessedMoveRef = useRef<string>('');
  const isOurFenLoadRef = useRef(false);
  const bonusTurnStartFromRef = useRef<{ row: number; col: number } | null>(null);
  // Ref for latest gameState (needed by replay timeout callbacks)
  const gameStateRef = useRef(gameState);
  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);
  // Ref for replay timeout chain
  const replayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isKingGuesser = currentPuzzle?.type === 'kingGuesser';

  const isPreviouslyCompleted = currentPuzzle
    ? (userProfile?.completedPuzzleIds ?? []).includes(currentPuzzle.id)
    : false;

  // Initialize board with empty position on first render
  useEffect(() => {
    const engine = ZodiacEngineService.zodiacEngine;
    isOurFenLoadRef.current = true;
    engine.loadFromFEN('8/8/8/8/8');
    engine.setGameMode(Mode.Sandbox);
  }, []);

  // Cleanup replay timeouts on unmount
  useEffect(() => {
    return () => {
      if (replayTimeoutRef.current) clearTimeout(replayTimeoutRef.current);
    };
  }, []);

  // Move detection: watch for moves made via the native Board interaction (Best Move only)
  useEffect(() => {
    if (isOurFenLoadRef.current) {
      isOurFenLoadRef.current = false;
      return;
    }
    if (puzzleStatus !== 'playing' || !moveSequence) return;
    if (animatingOpponent) return;
    // King Guesser uses onSquareClick, not move detection
    if (isKingGuesser) return;

    const lastMove = (gameState as any).lastMove;
    if (!lastMove) return;

    // Bonus turn handling: skip validation while piece still has bonus moves
    // (e.g., Monkey swing chains — validate only after final move)
    if (gameState.activeBonusTurnPieceId) {
      if (!bonusTurnStartFromRef.current) {
        bonusTurnStartFromRef.current = lastMove.fromPosition;
      }
      return;
    }

    // Deduplicate — engine fires multiple notifications per move
    const moveKey = `${lastMove.fromPosition.row},${lastMove.fromPosition.col}>${lastMove.toPosition.row},${lastMove.toPosition.col}:${lastMove.mover.type}`;
    if (moveKey === lastProcessedMoveRef.current) return;
    lastProcessedMoveRef.current = moveKey;

    // Use original "from" if we just finished a bonus turn chain
    const effectiveFrom = bonusTurnStartFromRef.current || lastMove.fromPosition;
    bonusTurnStartFromRef.current = null;

    // Validate against expected move (primary + alternatives)
    const expected = moveSequence[currentMoveIndex].playerMove;
    const alternatives = moveSequence[currentMoveIndex].alternativePlayerMoves || [];
    const allValid = [expected, ...alternatives];

    const isCorrect = allValid.some(valid =>
      effectiveFrom.row === valid.from.row &&
      effectiveFrom.col === valid.from.col &&
      lastMove.toPosition.row === valid.to.row &&
      lastMove.toPosition.col === valid.to.col
    );

    if (isCorrect) {
      handleCorrectMove();
    } else {
      handleWrongMove(lastMove.toPosition);
    }
  }, [gameState]); // eslint-disable-line

  const handleHome = () => {
    navigate('/');
  };

  const resetPuzzleState = () => {
    setCurrentMoveIndex(0);
    setWrongMovePos(null);
    setCorrectMovePos(null);
    setPuzzleStatus('idle');
    setCheckpointFen(null);
    setAttempts(0);
    setAnimatingOpponent(false);
    setKingGuessPhase(null);
    setCountdownNumber(null);
    setGuessedPos(null);
    lastProcessedMoveRef.current = '';
    isOurFenLoadRef.current = false;
    bonusTurnStartFromRef.current = null;
    if (replayTimeoutRef.current) {
      clearTimeout(replayTimeoutRef.current);
      replayTimeoutRef.current = null;
    }
  };

  const markKingsOnBoard = (puzzle: SavedPuzzle) => {
    if (puzzle.type !== 'bestMove') return;
    const gs = ZodiacEngineService.zodiacEngine.getGameState();
    for (const row of gs.board) {
      for (const sq of row) {
        for (const piece of sq.Pieces) {
          if (piece.isLight && piece.type === puzzle.lightKing) {
            piece.isKing = true;
          } else if (!piece.isLight && piece.type === puzzle.darkKing) {
            piece.isKing = true;
          }
        }
      }
    }
  };

  const handleCardClick = async (typeId: PuzzleTypeId) => {
    setSelectedType(typeId);
    setLoading(true);
    setCurrentPuzzle(null);
    setMoveSequence(null);
    setPuzzleStatus('idle');
    resetPuzzleState();

    try {
      const completedIds = userProfile?.completedPuzzleIds ?? [];
      const puzzle = await getRandomUnseenPuzzle(typeId, completedIds, currentPuzzle?.id);

      if (puzzle) {
        const engine = ZodiacEngineService.zodiacEngine;
        isOurFenLoadRef.current = true;
        engine.loadFromFEN(puzzle.fen);
        engine.setGameMode(Mode.Sandbox);

        // For bestMove puzzles, mark king pieces so crowns are visible (perfect information)
        markKingsOnBoard(puzzle);

        setCurrentPuzzle(puzzle);
        setCheckpointFen(puzzle.fen);

        const sequence = getNormalizedMoveSequence(puzzle);
        setMoveSequence(sequence);

        if (puzzle.type === 'bestMove' && sequence) {
          setPuzzleStatus('playing');
        } else if (puzzle.type === 'kingGuesser') {
          setPuzzleStatus('playing');
          setKingGuessPhase('countdown');
          setCountdownNumber(3);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Solving a puzzle is that mode's victory, so it gets the same cue the games do.
  // Placed here rather than at the three setPuzzleStatus('complete') call sites,
  // which all pair with this one function.
  const markPuzzleComplete = async () => {
    playVictorySound();
    if (user && currentPuzzle) {
      try {
        await UserDataService.addCompletedPuzzleId(user.uid, currentPuzzle.id);
      } catch {
        // Silent fail
      }
    }
  };

  const handleCorrectMove = () => {
    if (!moveSequence) return;
    const currentRound = moveSequence[currentMoveIndex];

    // Flash green checkmark at move destination
    const lastMove = (gameState as any).lastMove;
    if (lastMove) {
      setCorrectMovePos(lastMove.toPosition);
      setTimeout(() => setCorrectMovePos(null), 800);
    }

    if (currentRound.opponentResponse) {
      // Opponent has a response — animate it after a delay
      setAnimatingOpponent(true);
      const oMove = currentRound.opponentResponse;

      setTimeout(async () => {
        const engine = ZodiacEngineService.zodiacEngine;
        const board = gameState.board;
        const oFromSq = board[oMove.from.row][oMove.from.col];
        const oPiece = oFromSq.Pieces[0];

        if (oPiece) {
          // Get valid moves to find matching move with full side effect data
          const validMoves = engine.getValidMoves(oMove.from, oPiece);
          const matchingMove = validMoves.find(
            m => m.position.row === oMove.to.row && m.position.col === oMove.to.col
          );

          if (matchingMove) {
            // Execute through engine — triggers all side effects (zombify, poison, etc.)
            isOurFenLoadRef.current = true;
            await engine.makeMove(
              oMove.from,
              matchingMove.position,
              oPiece,
              matchingMove.toPiece,
              matchingMove.moveType,
              matchingMove.sideEffects
            );
          } else {
            // Fallback: raw mutation if no valid move found
            const oToSq = board[oMove.to.row][oMove.to.col];
            oFromSq.Pieces = [];
            oToSq.Pieces = [oPiece];
            const afterOpponentFen = ZodiacFEN.encode(gameState);
            isOurFenLoadRef.current = true;
            engine.loadFromFEN(afterOpponentFen);
            engine.setGameMode(Mode.Sandbox);
          }
        }

        // Save checkpoint from current engine state
        const updatedState = engine.getGameState();
        const checkpointFenStr = ZodiacFEN.encode(updatedState);
        setCheckpointFen(checkpointFenStr);
        setAnimatingOpponent(false);

        const nextIdx = currentMoveIndex + 1;
        if (nextIdx >= moveSequence.length) {
          setPuzzleStatus('complete');
          markPuzzleComplete();
        } else {
          setCurrentMoveIndex(nextIdx);
        }
      }, 600);
    } else {
      // No opponent response — save checkpoint and advance
      const newCheckpoint = ZodiacFEN.encode(gameState);
      setCheckpointFen(newCheckpoint);

      const nextIdx = currentMoveIndex + 1;
      if (nextIdx >= moveSequence.length) {
        setPuzzleStatus('complete');
        markPuzzleComplete();
      } else {
        setCurrentMoveIndex(nextIdx);
      }
    }
  };

  const handleWrongMove = (toPosition: { row: number; col: number }) => {
    setWrongMovePos(toPosition);
    setAttempts((prev) => prev + 1);

    const resetFen = checkpointFen;
    setTimeout(() => {
      setWrongMovePos(null);
      if (resetFen) {
        const engine = ZodiacEngineService.zodiacEngine;
        isOurFenLoadRef.current = true;
        engine.loadFromFEN(resetFen);
        engine.setGameMode(Mode.Sandbox);
        if (currentPuzzle) markKingsOnBoard(currentPuzzle);
      }
    }, 1200);
  };

  // ===== King Guesser: countdown, replay, guess =====

  // Countdown: 3 → 2 → 1 → start replay
  useEffect(() => {
    if (kingGuessPhase !== 'countdown' || countdownNumber === null) return;

    if (countdownNumber > 1) {
      replayTimeoutRef.current = setTimeout(() => {
        setCountdownNumber(countdownNumber - 1);
      }, 900);
    } else {
      replayTimeoutRef.current = setTimeout(() => {
        setCountdownNumber(null);
        if (currentPuzzle) {
          const sequence = getNormalizedMoveSequence(currentPuzzle);
          if (sequence && sequence.length > 0) {
            setKingGuessPhase('replay');
            startReplay(sequence);
          } else {
            setKingGuessPhase('guessing');
          }
        }
      }, 900);
    }
  }, [kingGuessPhase, countdownNumber]); // eslint-disable-line

  const applyReplayMove = useCallback((move: MoveStep) => {
    const board = gameStateRef.current.board;
    const fromSq = board[move.from.row][move.from.col];
    const toSq = board[move.to.row][move.to.col];
    const pieceIdx = fromSq.Pieces.findIndex(
      (p: any) => p.type === move.pieceType && p.isLight === move.pieceIsLight
    );
    const piece = pieceIdx >= 0 ? fromSq.Pieces[pieceIdx] : fromSq.Pieces[0];
    if (!piece) return;
    if (pieceIdx >= 0) fromSq.Pieces.splice(pieceIdx, 1);
    else fromSq.Pieces = [];
    toSq.Pieces = [piece];

    // Force React re-render with mutated board — avoid loadFromFEN which
    // temporarily resets mode to Dev and can trigger the AI scheduler
    isOurFenLoadRef.current = true;
    updateGameState({ ...gameStateRef.current });
  }, [updateGameState]);

  const startReplay = useCallback((sequence: MoveRound[]) => {
    const playStep = (roundIdx: number, isOpponent: boolean) => {
      if (roundIdx >= sequence.length) {
        setKingGuessPhase('guessing');
        return;
      }
      const round = sequence[roundIdx];
      const move = isOpponent ? round.opponentResponse : round.playerMove;

      if (!move) {
        if (roundIdx + 1 < sequence.length) {
          replayTimeoutRef.current = setTimeout(() => playStep(roundIdx + 1, false), 800);
        } else {
          replayTimeoutRef.current = setTimeout(() => setKingGuessPhase('guessing'), 800);
        }
        return;
      }

      applyReplayMove(move);

      if (!isOpponent && round.opponentResponse) {
        replayTimeoutRef.current = setTimeout(() => playStep(roundIdx, true), 800);
      } else if (roundIdx + 1 < sequence.length) {
        replayTimeoutRef.current = setTimeout(() => playStep(roundIdx + 1, false), 800);
      } else {
        replayTimeoutRef.current = setTimeout(() => setKingGuessPhase('guessing'), 800);
      }
    };

    playStep(0, false);
  }, [applyReplayMove]);

  const handleKingGuessClick = useCallback((position: { row: number; col: number }) => {
    if (kingGuessPhase !== 'guessing' || !currentPuzzle) return;

    const square = gameStateRef.current.board[position.row][position.col];
    if (square.Pieces.length === 0) return;

    const topPiece = square.Pieces[square.Pieces.length - 1];
    // Only allow guessing pieces of the side they're looking for
    if (topPiece.isLight !== currentPuzzle.playerIsLight) return;

    // Highlight the guessed piece immediately
    setGuessedPos(position);

    const expectedKingType = currentPuzzle.playerIsLight
      ? currentPuzzle.lightKing
      : currentPuzzle.darkKing;

    if (topPiece.type === expectedKingType) {
      setPuzzleStatus('complete');
      markPuzzleComplete();
    } else {
      // Wrong — show highlight briefly, then X, then skip to next puzzle
      setAttempts((prev) => prev + 1);
      setTimeout(() => {
        setWrongMovePos(position);
      }, 400);
      setTimeout(() => {
        setWrongMovePos(null);
        setGuessedPos(null);
        if (selectedType) handleCardClick(selectedType);
      }, 1600);
    }
  }, [kingGuessPhase, currentPuzzle, selectedType]); // eslint-disable-line

  const getStatusText = () => {
    if (isKingGuesser) {
      if (puzzleStatus === 'complete') return 'Correct!';
      if (kingGuessPhase === 'countdown') return 'Get ready...';
      if (kingGuessPhase === 'replay') return 'Watching...';
      if (kingGuessPhase === 'guessing') return 'Guess now!';
      return '\u2014';
    }
    if (puzzleStatus === 'playing') return 'In Progress';
    if (puzzleStatus === 'complete') return 'Solved!';
    return '\u2014';
  };

  // No-op handlers for Board (required props, prevent browser default drag)
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <AdSenseLayout>
      <div className={`${styles.container} ${theme === 'dark' ? styles.dark : ''} ${globalStyles.app}`}>
        <WispyStars />
        <GlobalHeader />

        {/* Home button */}
        <button
          className={styles.homeBackButton}
          onClick={() => { playUISound(); handleHome(); }}
          aria-label={t('common.home')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        {/* Main content area */}
        <div className={styles.puzzlePageContent}>
          {/* Title */}
          <h1 className={styles.selectionTitle}>Puzzles</h1>

          {/* Puzzle type cards — compact row */}
          <div className={styles.puzzleTopBar}>
            {PUZZLE_TYPES.map((pt) => (
              <button
                key={pt.id}
                className={`${styles.puzzleCard} ${selectedType === pt.id ? styles.puzzleCardActive : ''}`}
                onClick={() => handleCardClick(pt.id)}
              >
                <div className={styles.puzzleCardTitle}>{pt.title}</div>
                <div className={styles.puzzleCardDescription}>{pt.description}</div>
              </button>
            ))}
          </div>

          {/* Puzzle info — always rendered to prevent board jump */}
          <div className={styles.puzzleInfo}>
            {loading ? (
              <>
                <div className={styles.puzzleInfoName}>Loading puzzle...</div>
                <div className={styles.puzzleInfoMeta}>&nbsp;</div>
              </>
            ) : currentPuzzle ? (
              <>
                <div className={styles.puzzleInfoName}>{currentPuzzle.name}</div>
                <div className={styles.puzzleInfoMeta}>
                  {currentPuzzle.type === 'bestMove' ? 'Find the Best Move' : 'Guess the King'}
                  {currentPuzzle.totalMoves && currentPuzzle.totalMoves > 1
                    ? ` · ${currentPuzzle.totalMoves} moves`
                    : ''}
                </div>
              </>
            ) : (
              <>
                <div className={styles.puzzleInfoName}>&nbsp;</div>
                <div className={styles.puzzleInfoMeta}>
                  {selectedType === null ? 'Select a puzzle type to begin' : '\u00A0'}
                </div>
              </>
            )}
          </div>
          {/* Move progress indicator — always reserve space to prevent board jump */}
          <div className={styles.moveProgressBar} style={
            puzzleStatus === 'idle' || isKingGuesser || !(moveSequence && moveSequence.length > 1)
              ? { visibility: 'hidden' }
              : undefined
          }>
            {moveSequence && moveSequence.length > 1 && puzzleStatus !== 'idle' && !isKingGuesser && moveSequence.map((_, idx) => {
              const isCompleted = puzzleStatus === 'complete' ? true : idx < currentMoveIndex;
              const isCurrent = puzzleStatus !== 'complete' && idx === currentMoveIndex;
              return (
                <React.Fragment key={idx}>
                  {idx > 0 && <div className={`${styles.moveProgressLine} ${isCompleted ? styles.moveProgressLineCompleted : ''}`} />}
                  <div className={`${styles.moveProgressStep} ${isCompleted ? styles.moveProgressStepCompleted : ''} ${isCurrent ? styles.moveProgressStepCurrent : ''}`}>
                    {isCompleted ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <span>{idx + 1}</span>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Game area: left panel + board */}
          <div className={styles.puzzleGameArea}>
            <div className={styles.boardWrapper}>
            {/* Left panel — puzzle status */}
            {selectedType && (
              <div className={styles.leftPanel} style={{ visibility: currentPuzzle && !loading ? 'visible' : 'hidden' }}>
                {isPreviouslyCompleted && (
                  <div className={styles.completedBadge}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Completed
                  </div>
                )}

                <div className={styles.panelSection}>
                  <div className={styles.panelLabel}>Status</div>
                  <div className={`${styles.panelValue} ${puzzleStatus === 'complete' ? styles.panelValueSuccess : ''}`}>
                    {getStatusText()}
                  </div>
                </div>

                {/* Best Move: progress */}
                {!isKingGuesser && moveSequence && moveSequence.length > 1 && (
                  <div className={styles.panelSection}>
                    <div className={styles.panelLabel}>Progress</div>
                    <div className={styles.panelValue}>
                      {puzzleStatus === 'complete'
                        ? `${moveSequence.length} / ${moveSequence.length}`
                        : `${currentMoveIndex + 1} / ${moveSequence.length}`}
                    </div>
                  </div>
                )}

                {/* Best Move: mistakes */}
                {!isKingGuesser && attempts > 0 && (
                  <div className={styles.panelSection}>
                    <div className={styles.panelLabel}>Mistakes</div>
                    <div className={styles.panelValue}>{attempts}</div>
                  </div>
                )}

                <div className={styles.panelSection}>
                  <div className={styles.panelLabel}>
                    {isKingGuesser ? 'Find the' : 'Playing as'}
                  </div>
                  <div className={styles.panelValue}>
                    {isKingGuesser
                      ? `${currentPuzzle?.playerIsLight ? 'Light' : 'Dark'} King`
                      : currentPuzzle?.playerIsLight ? 'Light' : 'Dark'}
                  </div>
                </div>
              </div>
            )}

            {/* Board — uses native piece selection and move system */}
            <div className={styles.boardContainer}>
              <Board
                gameState={gameState}
                uiState={{
                  potentialMoves: potentialMoves,
                  potentialSideEffects: potentialSideEffects,
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                designateMode={isKingGuesser || undefined}
                onSquareClick={isKingGuesser ? handleKingGuessClick : undefined}
                puzzlePlayerIsLight={!isKingGuesser && currentPuzzle ? currentPuzzle.playerIsLight : undefined}
              />

              {/* King Guesser: countdown overlay */}
              {isKingGuesser && countdownNumber !== null && (
                <div className={styles.countdownOverlay}>
                  <div key={countdownNumber} className={styles.countdownNumber}>
                    {countdownNumber}
                  </div>
                </div>
              )}

              {/* King Guesser: guess prompt */}
              {isKingGuesser && kingGuessPhase === 'guessing' && puzzleStatus !== 'complete' && (
                <div className={styles.phaseIndicator}>
                  Click the {currentPuzzle?.playerIsLight ? 'Light' : 'Dark'} King!
                </div>
              )}

              {/* King Guesser: selection highlight */}
              {guessedPos && (
                <div
                  className={styles.guessHighlight}
                  style={{
                    left: `${guessedPos.col * 20}%`,
                    top: `${(7 - guessedPos.row) * 12.5}%`,
                    width: '20%',
                    height: '12.5%',
                  }}
                />
              )}

              {/* Wrong move/guess X marker */}
              {wrongMovePos && (
                <div
                  className={styles.wrongMoveMarker}
                  style={{
                    left: `${wrongMovePos.col * 20}%`,
                    top: `${(7 - wrongMovePos.row) * 12.5}%`,
                    width: '20%',
                    height: '12.5%',
                  }}
                >
                  <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </div>
              )}

              {/* Correct move checkmark marker */}
              {correctMovePos && (
                <div
                  className={styles.correctMoveMarker}
                  style={{
                    left: `${correctMovePos.col * 20}%`,
                    top: `${(7 - correctMovePos.row) * 12.5}%`,
                    width: '20%',
                    height: '12.5%',
                  }}
                >
                  <svg width="60%" height="60%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              )}

              {/* Puzzle complete overlay */}
              {puzzleStatus === 'complete' && (
                <div className={styles.puzzleCompleteOverlay}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <div className={styles.puzzleCompleteText}>
                    {isKingGuesser ? 'Correct!' : 'Puzzle Solved!'}
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>

          {/* Next puzzle button */}
          {selectedType && !loading && (
            <button
              className={styles.nextPuzzleButton}
              onClick={() => handleCardClick(selectedType)}
            >
              Next Puzzle
            </button>
          )}
        </div>
      </div>
    </AdSenseLayout>
  );
}

export default Puzzles;
